import { deleteToken, getToken, messaging, onMessage } from '@/plugins/firebaseConfig'
import { messageService } from '@/api/message'

export const usePushManager = () => {
  /**
   * @description 请求通知权限 及 获取 FCM Token 并保存
   */
  const requestNotificationPermission = async (needOpen = false) => {
    try {
      await Notification.requestPermission()

      const userStore = useUserStore()
      let token = userStore.fcmToken

      if (!userStore.fcmToken) {
        try {
          // 检查 messaging 是否可用
          if (!messaging) {
            console.warn('Firebase messaging is not available')
            return token
          }

          token = await getToken(messaging, { vapidKey: import.meta.env.VITE_APP_VAPID_KEY })
          userStore.SET_FCM_TOKEN(token)

          // 添加超时处理
          const tokenBindPromise = messageService.userTokenBind({
            fcmToken: token,
            userAgentKey: userStore.userAgentKey,
          })

          // 设置 10 秒超时
          const tokenBindTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Token bind timeout')), 10000)
          })

          await Promise.race([tokenBindPromise, tokenBindTimeoutPromise])
        } catch (error) {
          console.error('Failed to get FCM token:', error)
          // 即使获取 FCM token 失败，也继续执行后续逻辑
        }
      }

      if (needOpen) {
        try {
          const { setNotification, getNotification, fansIds, creatorIds } = usePushNotification()
          const { isCreator } = storeToRefs(userStore)

          // 添加超时处理
          const getNotificationPromise = getNotification()
          const getNotificationTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Get notification timeout')), 10000)
          })

          const res = (await Promise.race([
            getNotificationPromise,
            getNotificationTimeoutPromise,
          ])) as any
          const additionalInfo = res.additionalInfo?.length
            ? res.additionalInfo
            : isCreator.value
              ? creatorIds
              : fansIds

          // 添加超时处理
          const setNotificationPromise = setNotification({
            configItem: 0,
            configValue: 'open',
            additionalInfo,
          })

          // 设置 10 秒超时
          const setNotificationTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Set notification timeout')), 10000)
          })

          await Promise.race([setNotificationPromise, setNotificationTimeoutPromise])
        } catch (error) {
          console.error('Failed to set notification config:', error)
          // 即使设置通知配置失败，也返回 token
        }
      }

      return token
    } catch (error) {
      console.error('Error in requestNotificationPermission:', error)
      return null
    }
  }
  /**
   * @description 监听 FCM 消息(前台)
   */
  function listenForMessages() {
    onMessage(messaging, (payload: any) => {
      if (payload) {
        const notification = new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: payload.notification.icon,
          data: {
            url: payload.data?.url || '/notifications',
          },
        })

        notification.onclick = event => {
          event.preventDefault()
          notification.close()
          navigator.serviceWorker.ready.then(registration => {
            window.open(`${registration.scope}notifications`, '_self')
          })
        }
      }
    })
  }

  /**
   * @description 删除 FCM 令牌函数
   **/
  const unsubscribeFromNotifications = async () => {
    try {
      // 获取当前的 Firebase Token
      const token = await getToken(messaging, { vapidKey: import.meta.env.VITE_APP_VAPID_KEY })
      if (token) {
        const userStore = useUserStore()
        await deleteToken(messaging)
        await messageService.userTokenUnbind({
          userAgentKey: userStore.userAgentKey,
        })
        userStore.SET_FCM_TOKEN('')
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error)
    }
  }

  /**
   * @description 检查应用程序是否从PWA桌面/主屏幕启动
   * @returns true or false
   */
  const isFromDesktopPWA = (() => {
    // 为 iOS Safari 的 standalone 模式添加类型支持
    const navigator = window.navigator as Navigator & { standalone?: boolean }
    return navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
  })()

  return {
    requestNotificationPermission,
    listenForMessages,
    unsubscribeFromNotifications,
    isFromDesktopPWA,
  }
}

export const useNotificationPermission = () => {
  const permission = ref()

  if ('Notification' in window) {
    permission.value = Notification.permission
  }

  let intervalId: any
  const checkPermission = () => {
    console.log(Notification.permission)
    if (Notification.permission !== permission.value) {
      permission.value = Notification.permission
    }
  }

  // if (permission.value === 'granted') {
  //   clearInterval(intervalId)
  // }

  // onMounted(() => {
  //   checkPermission()
  // intervalId = setInterval(checkPermission, 1000)
  // if (permission.value === 'granted') {
  //   clearInterval(intervalId)
  // }
  // })

  // onUnmounted(() => {
  //   clearInterval(intervalId)
  // })

  return { permission }
}

export const usePushNotification = () => {
  /**
   * @description 设置通知
   * @param data
   * configItem: 0, // 0: 通知设置 1-email
   * configValue: 'open' | 'close', // 打开或关闭
   * additionalInfo: any[] // 通知信息选项
   */
  const setNotification = async (data: {
    configItem: number
    configValue: 'open' | 'close'
    additionalInfo: any[]
  }) => {
    try {
      await messageService.customConfigSet(data)
    } catch (error: any) {
      console.log(error)
    }
  }

  /**
   * @description 获取通知
   * @param configItem 0: 通知设置
   */
  const getNotification = async (configItem = 0) => {
    const res = await messageService.customConfigQuery({ configItem })
    return res
  }

  const fansIds = [10, 11]
  const creatorIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]

  return {
    setNotification,
    getNotification,
    fansIds,
    creatorIds,
  }
}
